/**
 * Account-settings registry.
 *
 * Single source of truth for every per-tenant setting. The Settings modal
 * renders form fields by walking this list; backend/UI consumers read
 * settings via the helpers below. To add a new setting, append a new
 * SettingDef here — no other code change is required for the modal to pick
 * it up.
 *
 * Two flavors:
 *   togglable: false  -> always present (e.g. GL format, every tenant has GLs)
 *   togglable: true   -> opt-in per account (e.g. state tax code, only some tenants)
 *
 * Stored shape on accounts.settings:
 *   {
 *     gl_format_example: "010-441-5-250-00",     <- field values
 *     gl_format_regex:   "^\\d{3}-...",
 *     state_tax_code:    "NC",
 *     features: {                                  <- toggle states
 *       state_tax_code: true                       (key matches SettingDef.key)
 *     }
 *   }
 */

export type FieldType = 'text';

export interface FieldDef {
  key: string;                    // localStorage key under settings.{key}
  type: FieldType;
  label: string;
  placeholder?: string;
  helpText?: string;
  maxLength?: number;
  uppercase?: boolean;
  monospace?: boolean;
}

export interface SettingDef {
  key: string;                    // identifier for this feature group
  category: string;               // section header in the modal
  label: string;
  description?: string;
  togglable: boolean;
  defaultEnabled?: boolean;       // initial state for new accounts (only used if togglable)
  fields: FieldDef[];
  // Optional transform on save: take the user's input and produce additional
  // derived keys (used by GL format to generate regex/placeholder/label from
  // a single example).
  derive?: (values: Record<string, string>) => Record<string, string>;
}

// ---------------- The registry ----------------

export const ACCOUNT_SETTINGS: SettingDef[] = [
  {
    key: 'gl_format',
    category: 'GL Account Format',
    label: 'GL Account Format',
    description:
      'Defines what a valid GL account ID looks like for this tenant. The Invoice Edit form ' +
      'uses this to validate every line item GL ID. Type one valid example; the system ' +
      'derives the validation pattern + placeholder + error message.',
    togglable: false,
    fields: [
      {
        key: 'gl_format_example',
        type: 'text',
        label: 'Example GL Account',
        placeholder: '010-441-5-250-00',
        helpText: 'A real-looking GL account number. Leave blank to skip strict validation.',
        monospace: true,
      },
    ],
    derive: (values) => {
      const example = (values.gl_format_example || '').trim();
      if (!example) {
        return {
          gl_format_example: '',
          gl_format_regex: '',
          gl_format_placeholder: '',
          gl_format_label: '',
        };
      }
      let regex = '^';
      let placeholder = '';
      let i = 0;
      while (i < example.length) {
        const c = example[i];
        if (/\d/.test(c)) {
          let count = 0;
          while (i < example.length && /\d/.test(example[i])) {
            count++;
            placeholder += 'X';
            i++;
          }
          regex += `\\d{${count}}`;
        } else {
          regex += c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          placeholder += c;
          i++;
        }
      }
      regex += '$';
      return {
        gl_format_example: example,
        gl_format_regex: regex,
        gl_format_placeholder: placeholder,
        gl_format_label: `Format must be ${placeholder} (e.g. ${example})`,
      };
    },
  },
  {
    key: 'state_tax_code',
    category: 'Tax',
    label: 'State Tax Code',
    description:
      'Two-letter state/province code (e.g. NC, VA). Used in tax handling on invoices for ' +
      'tenants whose chart of accounts cares about state-level tax codes. Disable for ' +
      'tenants that handle this differently.',
    togglable: true,
    defaultEnabled: false,
    fields: [
      {
        key: 'state_tax_code',
        type: 'text',
        label: 'State Tax Code',
        placeholder: 'NC',
        maxLength: 4,
        uppercase: true,
      },
    ],
  },
  {
    key: 'voucher_requisitions',
    category: 'AP Workflow',
    label: 'Voucher Requisitions',
    description:
      'Shows the per-invoice "Needs Approval" checkbox and the per-user "Requires Approval" toggle. ' +
      'Turn on for tenants that route invoices through an approval queue in Keystone before payment. ' +
      'Leave off if the tenant has no approval workflow.',
    togglable: true,
    defaultEnabled: false,
    fields: [],
  },
  {
    key: 'po_matching',
    category: 'PO',
    label: 'PO Line Matching',
    description:
      'Shows the "Match PO Lines" button on invoices with a PO number, and lets clerks match each ' +
      'invoice line to a specific PO detail record. Recommended for tenants using blanket POs. ' +
      'Rollout is phased — enable per-tenant only after training.',
    togglable: true,
    defaultEnabled: false,
    fields: [],
  },
];

// ---------------- Helpers ----------------

type SettingsBlob = Record<string, unknown> | undefined | null;

interface AccountLike {
  settings?: SettingsBlob;
}

const findDefByFieldKey = (fieldKey: string): SettingDef | undefined =>
  ACCOUNT_SETTINGS.find((s) => s.fields.some((f) => f.key === fieldKey));

/**
 * Is a togglable setting enabled for this account?
 * Non-togglable settings always return true.
 *
 * The features map on account.settings is the source of truth. If the key is
 * absent (never set explicitly via the Account Settings modal), we fall back
 * to the setting's `defaultEnabled` — NOT to inferring from whether the
 * underlying value happens to be populated.
 *
 * (Previously we inferred enabled-ness from field presence so that legacy
 * accounts like Cleveland — which had state_tax_code = "NC" set before the
 * features map existed — kept the feature on. That inference made it
 * impossible to disable a feature for a tenant whose stored value was still
 * non-empty, because the toggle would silently re-enable on every page load.
 * Opt-in is now explicit: set features.{key} = true via the settings modal.)
 */
export function isFeatureEnabled(
  account: AccountLike | null | undefined,
  settingKey: string,
): boolean {
  const def = ACCOUNT_SETTINGS.find((s) => s.key === settingKey);
  if (!def) return false;
  if (!def.togglable) return true;
  const settings = account?.settings;
  const features =
    settings && typeof settings === 'object'
      ? ((settings as Record<string, unknown>).features as
          | Record<string, unknown>
          | undefined)
      : undefined;
  if (features && settingKey in features) return !!features[settingKey];
  return def.defaultEnabled ?? false;
}

/**
 * Read a single field value, but only if the owning setting is enabled.
 * Returns undefined for disabled settings even if a value is stored.
 */
export function getSetting<T = unknown>(
  account: AccountLike | null | undefined,
  fieldKey: string,
): T | undefined {
  const def = findDefByFieldKey(fieldKey);
  if (!def) return undefined;
  if (!isFeatureEnabled(account, def.key)) return undefined;
  const settings = account?.settings;
  if (!settings || typeof settings !== 'object') return undefined;
  return (settings as Record<string, unknown>)[fieldKey] as T | undefined;
}
